import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  AccommodationProductType,
  ExposedActivityIngredient,
  ExposedHotelIngredient,
  OldExposedActivityIngredient,
  OldExposedHotelIngredient,
  SearchTerm,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  AccommodationType,
  AmenitiesFilter,
  BoardBasisFilter,
  ExperienceFilter,
  IngredientType,
  InputBoardBasisQL,
  SearchTermType,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import { toAvailabilityOrder } from '@libs/search-service-smart-search';
import type { Coordinate } from '@libs/constraint-builder';
import {
  PolygonConstraint,
  deserializeConstraint,
  serializeConstraint,
} from '@libs/constraint-builder';
import { isDefined } from '@libs/utils';

import { deserializeConstraintText } from '../ingredientSerializer/legacy/staySerializer/hotelSerializer';

import {
  createActivityIngredient,
  createExposedActivityIngredient,
} from './recipeSerializer.activity.helpers';
import { isAvailabilityOrderQl } from './recipeSerializer.helpers';

export function isOldExposedHotelIngredient(
  exposedHotelIngredient: ExposedHotelIngredient | OldExposedHotelIngredient
): exposedHotelIngredient is OldExposedHotelIngredient {
  return (
    'serializedConstraint' in exposedHotelIngredient ||
    'query' in exposedHotelIngredient ||
    'hearted' in exposedHotelIngredient
  );
}

export function createExposedHotelIngredient(
  hotelIngredient: HotelIngredient
): ExposedHotelIngredient {
  const { searchTerm, numberOfNights, filters, heartedHotels, subIngredients } =
    hotelIngredient;

  return {
    ingredientType: IngredientType.Hotel,
    destination:
      searchTerm.type === SearchTermType.FreeText
        ? searchTerm.freeText
        : undefined,
    polygonId:
      searchTerm.type === SearchTermType.PolygonId
        ? searchTerm.polygonId
        : undefined,
    hotelCodes:
      searchTerm.type === SearchTermType.HotelCode
        ? [searchTerm.hotelCode]
        : undefined,
    numberOfNights,
    starRating: filters.starRating > 0 ? filters.starRating : 0,
    selectedHotelTypes: filters.selectedHotelTypes.length
      ? filters.selectedHotelTypes
      : undefined,
    freeCancellation: filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    ),
    boardBasis: filters.boardBasis.length ? filters.boardBasis : undefined,
    hasParking:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Parking) ||
      undefined,
    petsAreAllowed:
      filters.selectedExperienceFilters.includes(
        ExperienceFilter.PetsAllowed
      ) || undefined,
    isBusinessProperty:
      filters.selectedExperienceFilters.includes(ExperienceFilter.Business) ||
      undefined,
    isFamilyProperty:
      filters.selectedExperienceFilters.includes(ExperienceFilter.Family) ||
      undefined,
    hasPool:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Pool) ||
      undefined,
    hasSpa:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Spa) ||
      undefined,
    hasGym:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Gym) ||
      undefined,
    hasInternet:
      filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Wifi) ||
      undefined,
    hasElectricVehicleChargingStation:
      filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.VehicleChargingStation
      ) || undefined,
    hasAirConditioning:
      filters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.AirConditioned
      ) || undefined,
    heartedHotels: heartedHotels.length ? heartedHotels : undefined,
    subIngredients: subIngredients.length
      ? subIngredients.map(createExposedActivityIngredient)
      : undefined,
  };
}

export function createHotelIngredient(
  exposedHotelIngredient: ExposedHotelIngredient | OldExposedHotelIngredient
): HotelIngredient {
  const { numberOfNights, starRating, subIngredients } = exposedHotelIngredient;
  const {
    serializedConstraint,
    query,
    apartments,
    hotels,
    housesAndCottages,
    parking,
    petsAllowed,
    pool,
    hearted,
  } = exposedHotelIngredient as OldExposedHotelIngredient;
  const {
    destination,
    hotelCodes: exposedHotelCodes,
    polygon: exposedPolygon,
    polygonId,
    selectedHotelTypes,
    hasParking,
    petsAreAllowed,
    isBusinessProperty,
    isFamilyProperty,
    hasPool,
    hasSpa,
    hasGym,
    hasAirConditioning,
    hasElectricVehicleChargingStation,
    hasInternet,
    heartedHotels,
    boardBasis,
    freeCancellation,
    suggestedBy,
  } = exposedHotelIngredient as ExposedHotelIngredient;

  const backwardsCompatibleHeartedHotels = hearted ?? heartedHotels;

  const hotelCodes = exposedHotelCodes?.filter(isDefined);
  const polygon = exposedPolygon?.filter(isDefined);

  const searchText = getSearchText({
    serializedConstraint,
    query,
    destination: destination ?? undefined,
    polygon,
  });

  let searchTerm: SearchTerm;

  if (hotelCodes?.[0]) {
    searchTerm = { type: SearchTermType.HotelCode, hotelCode: hotelCodes[0] };
  } else if (polygonId) {
    searchTerm = { type: SearchTermType.PolygonId, polygonId };
  } else if (destination?.includes('polygon-id:')) {
    searchTerm = {
      type: SearchTermType.PolygonId,
      polygonId: Number(destination.split(':')[1]),
    };
  } else if (searchText) {
    searchTerm = { type: SearchTermType.FreeText, freeText: searchText };
  } else if (backwardsCompatibleHeartedHotels?.[0]) {
    searchTerm = {
      type: SearchTermType.HotelCode,
      hotelCode: backwardsCompatibleHeartedHotels?.[0].code,
    };
  } else {
    throw new Error('No valid searchTerm found');
  }

  const hotelIngredient = new HotelIngredient(searchTerm);
  hotelIngredient.numberOfNights = numberOfNights ?? undefined;
  hotelIngredient.filters.starRating = starRating ?? 0;
  hotelIngredient.subIngredients =
    subIngredients?.map(
      (
        subIngredient: ExposedActivityIngredient | OldExposedActivityIngredient
      ) => createActivityIngredient(subIngredient, hotelIngredient.ingredientId)
    ) ?? [];

  const displayText = getDisplayText({
    destination: destination ?? undefined,
    query,
  });
  if (displayText) {
    hotelIngredient.displayText = displayText;
  }

  if (polygon) {
    hotelIngredient.polygonConstraint = new PolygonConstraint(polygon);
  }

  hotelIngredient.filters.selectedHotelTypes = selectedHotelTypes
    ? getNewSelectedHotelTypes(selectedHotelTypes)
    : getOldSelectedHotelTypes(hotels, apartments, housesAndCottages);

  hotelIngredient.filters.selectedAmenitiesFilters =
    getSelectedAmenitiesFilters(
      parking ?? hasParking ?? undefined,
      pool ?? hasPool ?? undefined,
      hasSpa ?? undefined,
      hasGym ?? undefined,
      hasInternet ?? undefined,
      hasAirConditioning ?? undefined,
      hasElectricVehicleChargingStation ?? undefined
    );

  hotelIngredient.filters.selectedExperienceFilters =
    getSelectedExperienceFilters(
      isBusinessProperty ?? undefined,
      petsAllowed ?? petsAreAllowed ?? undefined,
      isFamilyProperty ?? undefined
    );

  hotelIngredient.heartedHotels = backwardsCompatibleHeartedHotels ?? [];

  hotelIngredient.filters.selectedHotelRules = [];
  if (freeCancellation) {
    hotelIngredient.filters.selectedHotelRules.push(
      AccommodationRule.FreeCancellation
    );
  }

  hotelIngredient.filters.boardBasis = boardBasis
    ? getBoardBasisFilters(boardBasis)
    : [];

  if (suggestedBy) {
    hotelIngredient.availabilityOrder = isAvailabilityOrderQl(suggestedBy)
      ? toAvailabilityOrder(suggestedBy)
      : suggestedBy;
  }

  return hotelIngredient;
}

function getSearchText(backwardsCompatibleSearchTexts: {
  serializedConstraint?: string;
  query?: string;
  destination?: string;
  polygon?: Coordinate[];
}): string | undefined {
  const { serializedConstraint, query, destination, polygon } =
    backwardsCompatibleSearchTexts;

  if (serializedConstraint) {
    const constraint = deserializeConstraint(serializedConstraint);
    constraint.maxNumberOfHotels = undefined;

    return serializeConstraint(constraint);
  }

  if (query) {
    return new HotelIngredient(
      deserializeConstraintText(query).searchTerm
    ).getSearchText();
  }

  if (destination) {
    return new HotelIngredient(
      deserializeConstraintText(destination).searchTerm
    ).getSearchText();
  }

  if (polygon) {
    return serializeConstraint({ selection: new PolygonConstraint(polygon) });
  }
}

function getDisplayText(backwardsCompatibleSearchTexts: {
  query?: string;
  destination?: string;
}): string | undefined {
  const { query, destination } = backwardsCompatibleSearchTexts;

  if (query) {
    return deserializeConstraintText(query).displayText;
  }

  if (destination) {
    return deserializeConstraintText(destination).displayText;
  }
}

function getNewSelectedHotelTypes(
  selectedHotelTypes: (AccommodationType | SelectedHotelTypeQL)[]
): AccommodationProductType[] {
  const result: AccommodationProductType[] = [];

  if (
    selectedHotelTypes.includes(AccommodationType.Hotel) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.Hotel)
  ) {
    result.push(AccommodationType.Hotel);
  }

  if (
    selectedHotelTypes.includes(AccommodationType.Apartment) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.Apartment)
  ) {
    result.push(AccommodationType.Apartment);
  }

  if (
    selectedHotelTypes.includes(AccommodationType.HouseOrCottage) ||
    selectedHotelTypes.includes(SelectedHotelTypeQL.HouseOrCottage)
  ) {
    result.push(AccommodationType.HouseOrCottage);
  }

  return result;
}

function getOldSelectedHotelTypes(
  hotels?: boolean,
  apartments?: boolean,
  housesAndCottages?: boolean
): AccommodationProductType[] {
  const selectedHotelTypes: AccommodationProductType[] = [];

  if (hotels) {
    selectedHotelTypes.push(AccommodationType.Hotel);
  }
  if (apartments) {
    selectedHotelTypes.push(AccommodationType.Apartment);
  }
  if (housesAndCottages) {
    selectedHotelTypes.push(AccommodationType.HouseOrCottage);
  }

  return selectedHotelTypes;
}

function getSelectedAmenitiesFilters(
  hasParking?: boolean,
  hasPool?: boolean,
  hasSpa?: boolean,
  hasGym?: boolean,
  hasInternet?: boolean,
  hasAirConditioning?: boolean,
  hasElectricVehicleChargingStation?: boolean
): AmenitiesFilter[] {
  const selectedAmenitiesFilters: AmenitiesFilter[] = [];

  if (hasParking) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Parking);
  }
  if (hasPool) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Pool);
  }
  if (hasSpa) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Spa);
  }
  if (hasGym) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Gym);
  }
  if (hasInternet) {
    selectedAmenitiesFilters.push(AmenitiesFilter.Wifi);
  }
  if (hasAirConditioning) {
    selectedAmenitiesFilters.push(AmenitiesFilter.AirConditioned);
  }
  if (hasElectricVehicleChargingStation) {
    selectedAmenitiesFilters.push(AmenitiesFilter.VehicleChargingStation);
  }

  return selectedAmenitiesFilters;
}

function getSelectedExperienceFilters(
  isBusinessProperty?: boolean,
  petsAreAllowed?: boolean,
  isFamilyProperty?: boolean
): ExperienceFilter[] {
  const selectedExperienceFilters: ExperienceFilter[] = [];
  if (petsAreAllowed) {
    selectedExperienceFilters.push(ExperienceFilter.PetsAllowed);
  }
  if (isBusinessProperty) {
    selectedExperienceFilters.push(ExperienceFilter.Business);
  }
  if (isFamilyProperty) {
    selectedExperienceFilters.push(ExperienceFilter.Family);
  }

  return selectedExperienceFilters;
}

function getBoardBasisFilters(
  boardBasis: (BoardBasisFilter | InputBoardBasisQL)[]
): BoardBasisFilter[] {
  const result: BoardBasisFilter[] = [];

  if (
    boardBasis.includes(BoardBasisFilter.AllInclusive) ||
    boardBasis.includes(InputBoardBasisQL.AllInclusive)
  ) {
    result.push(BoardBasisFilter.AllInclusive);
  }

  if (
    boardBasis.includes(BoardBasisFilter.Breakfast) ||
    boardBasis.includes(InputBoardBasisQL.Breakfast)
  ) {
    result.push(BoardBasisFilter.Breakfast);
  }

  if (
    boardBasis.includes(BoardBasisFilter.FullBoard) ||
    boardBasis.includes(InputBoardBasisQL.FullBoard)
  ) {
    result.push(BoardBasisFilter.FullBoard);
  }

  if (
    boardBasis.includes(BoardBasisFilter.HalfBoard) ||
    boardBasis.includes(InputBoardBasisQL.HalfBoard)
  ) {
    result.push(BoardBasisFilter.HalfBoard);
  }

  if (
    boardBasis.includes(BoardBasisFilter.RoomOnly) ||
    boardBasis.includes(InputBoardBasisQL.RoomOnly)
  ) {
    result.push(BoardBasisFilter.RoomOnly);
  }
  return result;
}
